import type { LobbyEvent } from '@danda/shared';
import { notificationService } from '@/lib/notifications';
import { getModuleHeroUrl, getModuleHeroVideoUrl, getStaffAvatarUrl } from '@/lib/media';
import { getSpecialNpcEmoji } from '@danda/shared/utils/avatar-emoji';
import { toast } from '@/lib/toast';
import type { EventHandlerContext } from './lobby-event-types';
import { msgId } from './stream-utils';

const TAVIAN_AVATAR_URL = getStaffAvatarUrl('tavian-avatar.webp');

export function handleGameEvents(event: LobbyEvent, ctx: EventHandlerContext): boolean {
  const { get } = ctx;
  const state = get();

  switch (event.type) {
    case 'lobby:game-started': {
      console.log('[duel-loader] lobby:game-started received', {
        gameSessionId: event.gameSessionId,
        moduleId: event.moduleId,
        moduleName: event.moduleName,
        duelParticipants: event.duelParticipants,
      });
      // Quick-start reached its redirect (started or resumed), the CTA's
      // pending state is done regardless of how this start was triggered.
      if (state.quickStartPending) {
        state.setQuickStartPending(false);
      }
      // The hero overlay's moment is over once a game actually starts; clear
      // it so returning to the lobby later doesn't resurrect it.
      if (state.heroOverlayCharId) {
        state.setHeroOverlayCharId(null);
      }
      // Same for the post-creation WelcomePanel: a player who went straight
      // from the hero overlay into their first adventure never saw it (the
      // overlay and then the game-start transition both occlude it), and
      // surfacing it later on a return-to-lobby would read as a "welcome"
      // shown after they've already played, not right after creation.
      if (state.justCreatedCharacter) {
        state.setJustCreatedCharacter(false);
      }
      // Prefer the registry-resolved hero URLs the server sends; fall back to
      // the legacy filename convention (hero_gen.webp / hero.mp4) for older
      // servers that don't include them.
      const heroImage =
        event.moduleHeroImage ?? (event.moduleId ? getModuleHeroUrl(event.moduleId) : undefined);
      const heroVideo =
        event.moduleHeroVideo ??
        (event.moduleId ? getModuleHeroVideoUrl(event.moduleId) : undefined);
      // Inject module hero video into party chat (only if room exists)
      const partyRoomId = event.partyId ? `party-${event.partyId}` : null;
      const partyRoomExists = partyRoomId && state.chatRooms.some((r) => r.id === partyRoomId);
      if (heroVideo && partyRoomExists) {
        const heroVideoUrl = heroVideo;
        state.addMessage(partyRoomId, {
          id: msgId('portal'),
          senderId: 'tavian',
          senderName: 'Tavian',
          content: `[MODULE_IMAGE:${heroVideoUrl}]\nThe portal shimmers to life... your adventure awaits!`,
          timestamp: new Date(),
          type: 'tavian',
          avatar: TAVIAN_AVATAR_URL,
          avatarUrl: TAVIAN_AVATAR_URL,
          avatarEmoji: getSpecialNpcEmoji('tavian') ?? '🧙‍♂️',
        });
      }
      state.addNotification({
        id: `notif-${Date.now()}`,
        type: 'system',
        title: 'Game Started',
        message: 'The portal is open! Your adventure awaits.',
        timestamp: new Date(),
        isRead: false,
        data: {
          gameSessionId: event.gameSessionId,
          gameServerWsUrl: event.gameServerWsUrl,
          moduleName: event.moduleName,
          moduleHeroImage: heroImage,
          moduleHeroVideo: heroVideo,
          partyId: event.partyId,
          duelParticipants: event.duelParticipants,
        },
      });
      // Dedup by gameSessionId — if a duplicate `lobby:game-started` arrives
      // (server retry, reconnect replay) while a navigation is in flight,
      // re-setting the atom would re-fire the LobbyLayout effect, abort the
      // running IIFE, and risk double-`navigate()` if the original IIFE has
      // already passed its abort check. Dropping the duplicate is safe because
      // the in-flight nav targets the same session.
      if (state.pendingGameStart?.gameSessionId !== event.gameSessionId) {
        state.setPendingGameStart({
          gameSessionId: event.gameSessionId,
          gameServerWsUrl: event.gameServerWsUrl,
          moduleName: event.moduleName,
          moduleHeroImage: heroImage,
          moduleHeroVideo: heroVideo,
          partyId: event.partyId,
          duelParticipants: event.duelParticipants,
        });
      }
      if (typeof window !== 'undefined' && !document.hasFocus()) {
        notificationService.notify('Game Started!', {
          body: 'The portal is open! Your adventure awaits.',
          type: 'game_starting',
        });
      }
      return true;
    }

    case 'lobby:quick-start-result': {
      if (event.status === 'failed') {
        // Reset the CTA so the player can retry; surface why. started/
        // resumed keep pending=true until `lobby:game-started` lands (it
        // carries the actual redirect payload).
        state.setQuickStartPending(false);
        const message = event.message ?? 'The adventure could not start. Try again in a moment.';
        // Toast is the VISIBLE surface (nothing renders the notifications
        // list, and the hero overlay would occlude party chat anyway);
        // the notification entry keeps the failure queryable in state.
        toast.error(message);
        state.addNotification({
          id: `notif-${Date.now()}`,
          type: 'system',
          title: 'Adventure not started',
          message,
          timestamp: new Date(),
          isRead: false,
          data: { code: event.code },
        });
      }
      return true;
    }

    case 'lobby:chat-message':
      state.addMessage('main', {
        id: msgId('lobby'),
        senderId: event.message.userId,
        senderName: event.message.displayName,
        content: event.message.message,
        timestamp: new Date(event.message.timestamp),
        type: event.message.type === 'system' ? 'system' : 'user',
      });
      return true;

    case 'lobby:player-joined':
      return true;

    case 'lobby:player-left':
      return true;

    case 'lobby:cancelled':
      state.addNotification({
        id: `notif-${Date.now()}`,
        type: 'system',
        title: 'Lobby Cancelled',
        message: event.reason || 'The lobby has been cancelled.',
        timestamp: new Date(),
        isRead: false,
      });
      return true;

    case 'lobby:start-game-result':
      // Success has no result message (lobby:game-started signals it), so
      // reaching here always means a rejection (insufficient tokens,
      // recovery in progress, no module, foreign companions, etc). Reuses
      // the 'Lobby Cancelled' notification title so LobbyLayout's existing
      // watch effect clears the game-start overlay immediately instead of
      // waiting out its 30s backstop, mirroring mobile's
      // resetStartGamePending.
      state.addNotification({
        id: `notif-${Date.now()}`,
        type: 'system',
        title: 'Lobby Cancelled',
        message: event.message || 'Failed to start the adventure.',
        timestamp: new Date(),
        isRead: false,
        data: { code: event.code },
      });
      return true;

    default:
      return false;
  }
}
